import React, { useCallback, useMemo, useState } from 'react';
import { Button, Grid, makeStyles } from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import _difference from 'lodash/difference';
import _isEmpty from 'lodash/isEmpty';
import _union from 'lodash/union';
import _without from 'lodash/without';
import Modal from '../../common/Modal';
import service from '../../../apis/service';
import { apiEndpoints } from '../../../constants/apiEndpoints';
import { getPlanDetails, showProgress } from '../../../redux/actions/DealershipActions';
import { ErrorToast, notifySuccess } from '../../utils/Utils';
import PopupTopBar from './PopupTopBar';
import AddDealerships from './AddDealerships';
import BottomSheetContainer from './BottomSheetContainer';
import useDealershipFetch from '../hooks/useDealershipFetch';
import useFilterOptions from '../hooks/useFilterOptions';

const useStyles = makeStyles((theme) => ({
  popupContainer: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    background: 'rgba(242, 244, 247, 1)',
  },
  bodyArea: {
    position: 'relative',
    flex: 1,
    minHeight: 0,
    width: '100%',
    maxWidth: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  content: {
    flex: 1,
    minHeight: 0,
    width: '100%',
    maxWidth: '100%',
    overflow: 'hidden',
  },
  footer: {
    padding: '20px 24px',
    borderTop: '1px solid #E4E7EC',
    background: '#FFFFFF',
  },
  cancelBtn: {
    minWidth: '120px',
    marginRight: '16px',
  },
  saveBtn: {
    minWidth: '120px',
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.secondary.main,
    '&:hover': {
      backgroundColor: theme.palette.primary.main,
    },
  },
}));

const DEALERSHIP_FIELD_NAME = 'dealerships';
const REMOVE_ACTION = 'Remove';

const EditPlanDealershipPopup = ({ close }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const plandetails = useSelector((state) => state.getPlanDetails.plandetails);

  // IDs currently shown as selected in the UI (loaded pages only)
  const [data, setData] = useState({ dealerships: [] });
  // Snapshot of IDs that arrived from the API with selected=true — used to compute delta at save time
  const [preSelectedIds, setPreSelectedIds] = useState(new Set());
  // IDs the user explicitly removed while selectAllMode is active
  const [excludedIds, setExcludedIds] = useState(new Set());
  // True after "Unselect All" — signals the backend to clear all assignments
  const [isUnselectAll, setIsUnselectAll] = useState(false);
  const [selectAllMode, setSelectAllMode] = useState(false);
  const [isSheetOpen, setSheetOpen] = useState(false);

  const { filters, filterObjectInitial } = useFilterOptions();

  const handlePreselectedIds = useCallback((ids) => {
    setPreSelectedIds((prev) => new Set([...prev, ...ids]));
    setData((prev) => ({ ...prev, dealerships: _union(prev.dealerships, ids) }));
  }, []);

  // Called on every page-1 load (filter/search change) — reset all selection state
  const handleReset = useCallback(() => {
    setSelectAllMode(false);
    setExcludedIds(new Set());
    setIsUnselectAll(false);
    setData({ dealerships: [] });
  }, []);

  const { dealerships, totalCount, hasMore, fetchNextPage, applyFilters, onSearch, activeFilters } = useDealershipFetch(
    plandetails?.id,
    filterObjectInitial,
    {
      onPreselectedIds: handlePreselectedIds,
      onReset: handleReset,
    },
  );

  const toggleSheet = useCallback(() => setSheetOpen((prev) => !prev), []);

  const handleDataChange = useCallback(
    ({ target: { name, value, checked } }) => {
      if (name !== DEALERSHIP_FIELD_NAME) return;
      const id = Number.parseInt(value, 10);

      if (selectAllMode) {
        setExcludedIds((prev) => {
          const next = new Set(prev);
          if (checked) {
            next.delete(id);
          } else {
            next.add(id);
          }
          return next;
        });
        return;
      }

      setData((prev) => ({
        ...prev,
        dealerships: checked ? _union(prev.dealerships, [id]) : _without(prev.dealerships, id),
      }));
    },
    [selectAllMode],
  );

  const handleSelectAll = useCallback((checked) => {
    if (checked) {
      setSelectAllMode(true);
      setExcludedIds(new Set());
      setIsUnselectAll(false);
    } else {
      // Unselect All — clear everything, signal backend to wipe assignments
      setSelectAllMode(false);
      setExcludedIds(new Set());
      setIsUnselectAll(true);
      setData({ dealerships: [] });
    }
  }, []);

  // Bottom sheet remove — only active in individual mode (selectAllMode shows no cards)
  const handleRemoveDealership = useCallback((e, name) => {
    if (name !== REMOVE_ACTION) return;
    const rawId = e.currentTarget?.id ?? e.currentTarget?.getAttribute?.('data-id');
    if (!rawId) return;
    const numericId = Number.parseInt(String(rawId), 10);
    setData((prev) => ({ ...prev, dealerships: _without(prev.dealerships, numericId) }));
  }, []);

  const savePlanDealerships = async () => {
    if (!plandetails?.id) return;
    const assignUrl = apiEndpoints.PLAN_DEALER_ASSIGN.replace('ID', plandetails.id);
    dispatch(showProgress(true));
    try {
      let payload;

      if (selectAllMode) {
        // Mode 1 or Mode 2
        payload =
          excludedIds.size === 0
            ? { filters: activeFilters }
            : { filters: activeFilters, excludedIds: Array.from(excludedIds) };
      } else if (isUnselectAll) {
        // Mode 4 — explicit list (empty = clear all, or with re-additions after unselect)
        payload = { dealershipIds: data.dealerships };
      } else {
        // Mode 3 — delta: what changed from the initial pre-selected state
        const preSelectedArr = Array.from(preSelectedIds);
        const addedIds = _difference(data.dealerships, preSelectedArr);
        const removedIds = _difference(preSelectedArr, data.dealerships);
        payload = { addedIds, removedIds };
      }

      await service.post(assignUrl, payload);
      dispatch(getPlanDetails({ ...plandetails, dealershipsRefreshKey: Date.now() }));
      notifySuccess('Saved Successfully');
      close();
    } catch (error) {
      ErrorToast(error);
    }
    dispatch(showProgress(false));
  };

  const isSaveDisabled = !selectAllMode && !isUnselectAll && _isEmpty(data.dealerships);

  // Accurate selected count using the full pre-existing count from LeftPlanContent
  // plus any adds/removes the user made in this session.
  const displaySelectedCount = useMemo(() => {
    if (isUnselectAll) return data.dealerships.length;
    const preSelectedArr = Array.from(preSelectedIds);
    const added = _difference(data.dealerships, preSelectedArr).length;
    const removed = _difference(preSelectedArr, data.dealerships).length;
    return (plandetails?.assignedDealershipsCount ?? 0) + added - removed;
  }, [data.dealerships, preSelectedIds, plandetails?.assignedDealershipsCount, isUnselectAll]);

  return (
    <Modal isOpen height="95vh" width="1070px" padding="0px" overflow closePopup={close}>
      <Grid className={classes.popupContainer}>
        <PopupTopBar add={false} title="Dealerships" close={close} />
        <div className={classes.bodyArea}>
          <Grid item className={classes.content}>
            <AddDealerships
              data={data}
              handleDataChange={handleDataChange}
              handleSelectAll={handleSelectAll}
              filters={filters}
              filterObjectInitial={filterObjectInitial}
              dealerships={dealerships}
              totalDealershipCount={totalCount}
              displaySelectedCount={displaySelectedCount}
              onApplyFilters={applyFilters}
              onSearch={onSearch}
              selectAllMode={selectAllMode}
              excludedIds={excludedIds}
              hasMore={hasMore}
              fetchNextPage={fetchNextPage}
            />
          </Grid>
          <BottomSheetContainer
            data={data}
            isSheetOpen={isSheetOpen}
            toggleSheet={toggleSheet}
            handleRemove={handleRemoveDealership}
            selectAllMode={selectAllMode}
            totalCount={totalCount}
            excludedCount={excludedIds.size}
            displaySelectedCount={displaySelectedCount}
          />
        </div>
        <Grid container justifyContent="flex-end" alignItems="center" className={classes.footer}>
          <Button variant="contained" className={classes.cancelBtn} onClick={close}>
            Cancel
          </Button>
          <Button
            variant="contained"
            className={classes.saveBtn}
            onClick={savePlanDealerships}
            disabled={isSaveDisabled}
          >
            Save
          </Button>
        </Grid>
      </Grid>
    </Modal>
  );
};

export default EditPlanDealershipPopup;
